import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin } from 'rxjs';
import { ExportRequestDirection, ExportRequestService } from '../../services/ExportRequestService/export-request';
import { ProjectService } from '../../services/ProjectService/project';
import { CompanySummary, ExportRequestResponse, ExportRequestStatus } from '../../models/export-request.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { FilterSelect, FilterOption } from '../../shared/components/filter-select/filter-select';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';
import { SelectProjectDialog } from '../../dialogs/select-project-dialog/select-project-dialog';

const STATUSES: ExportRequestStatus[] = ['PREPARING', 'PENDING', 'ACCEPTED', 'DECLINED', 'TRACE_FAILED'];

@Component({
  selector: 'app-export-requests-inbox',
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    StatusBadge,
    ErrorBanner,
    EmptyState,
    FilterSelect,
    PageSizeSelect,
  ],
  templateUrl: './export-requests-inbox.html',
  styleUrl: './export-requests-inbox.scss',
})
export class ExportRequestsInbox implements OnInit {
  statusOptions: FilterOption[] = STATUSES.map((s) => ({
    value: s,
    label: s
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
  }));

  direction = signal<ExportRequestDirection>('incoming');
  statusFilter: ExportRequestStatus | '' = '';

  requests = signal<ExportRequestResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);
  totalPages = signal(1);
  totalCount = signal(0);

  private companyDirectory = signal<Map<string, CompanySummary>>(new Map());

  constructor(
    private exportRequestService: ExportRequestService,
    private projectService: ProjectService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  openRaiseDialog(): void {
    this.dialog
      .open(SelectProjectDialog, { width: '480px' })
      .afterClosed()
      .subscribe((projectId: string | undefined) => {
        if (projectId) {
          this.router.navigate(['/projects', projectId, 'export-requests', 'new']);
        }
      });
  }

  get rangeStart(): number {
    return this.totalCount() === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount());
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages();
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  ngOnInit(): void {
    this.load();
  }

  setDirection(direction: ExportRequestDirection): void {
    this.direction.set(direction);
    this.page.set(0);
    this.load();
  }

  onFilterChange(): void {
    this.page.set(0);
    this.load();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
    this.load();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages() || page === this.page()) {
      return;
    }
    this.page.set(page);
    this.load();
  }

  get directionLabel(): string {
    return this.direction() === 'incoming' ? 'Incoming' : 'Outgoing';
  }

  get statusFilterLabel(): string {
    if (!this.statusFilter) {
      return 'All statuses';
    }
    return this.statusOptions.find((o) => o.value === this.statusFilter)?.label ?? this.statusFilter;
  }

  counterparty(request: ExportRequestResponse): CompanySummary | undefined {
    const companyId = this.direction() === 'incoming' ? request.exporterCompanyId : request.importerCompanyId;
    return this.companyDirectory().get(companyId);
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.exportRequestService.inbox(this.direction(), this.statusFilter || undefined, this.page(), this.pageSize()).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.requests.set(res.data);
        this.loadCompanyDirectory(res.data);
        this.totalPages.set(res.page?.total_pages ?? 1);
        this.totalCount.set(res.page?.count ?? res.data.length);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load export requests.'));
      },
    });
  }

  private loadCompanyDirectory(requests: ExportRequestResponse[]): void {
    const projectIds = [...new Set(requests.map((r) => r.projectId))];
    if (projectIds.length === 0) {
      return;
    }

    forkJoin(projectIds.map((id) => this.projectService.listParticipants(id))).subscribe({
      next: (results) => {
        const directory = new Map<string, CompanySummary>();
        for (const res of results) {
          for (const p of res.data) {
            directory.set(p.companyId, { id: p.companyId, name: p.companyName, countryCode: p.countryCode });
          }
        }
        this.companyDirectory.set(directory);
      },
      error: () => {},
    });
  }
}
